import { useEffect, useRef, useState } from 'react';
import { useEditor, dirtyCount, dirtyPages } from '../state/store';
import { useCode } from '../state/code-store';
import { fetchStoreInfo, downloadTheme } from '../api/client';
import { ReviewsPanel } from './reviews/ReviewsPanel';

/**
 * Onde o botão "Ver loja" abre.
 *
 * REGRA NOVA (Nov/2025): o subdomínio `{uuid}.store-preview.lunacheckout.com`
 * é INTERNO (uso do editor) — o engine bloqueia acesso direto no
 * browser. Cliente final tem que apontar um domínio próprio via CNAME
 * pra atender a loja pública.
 *
 * Por isso o botão "Ver loja" agora consulta paycentral via
 * `GET /editor/.../store-info` ANTES de abrir:
 *   - Se `domain_store` está setado → abre `https://{domain_store}`
 *   - Se for null → mostra modal pedindo pra configurar
 *
 * Se a session já tem `storeHost` (passado via ?host=), usamos isso
 * sem precisar fazer fetch — significa que o editor já foi aberto via
 * o domínio do cliente, então sabemos que ele tem um.
 *
 * User pediu: "se ele nao tiver dominio apontado, so buscar da api
 * paycentral avisar que ele precisa apontar um dominio para ver a loja".
 */

const ADMIN_DOMAIN_URL = 'https://app.lunacheckout.com/configuracoes/dominios';

/**
 * Top bar — mirrors the Shopify 2024 theme editor layout:
 *  [ hamburger | store name | page picker ] ... [ device toggle ] ... [ undo/redo | status | Preview | Save ]
 */

function statusText(state: string, ts?: number): string {
  if (state === 'saving') return 'Salvando…';
  if (state === 'error') return 'Erro ao salvar';
  if (state === 'saved' && ts) {
    const diff = Math.round((Date.now() - ts) / 1000);
    if (diff < 5) return 'Salvo';
    if (diff < 60) return `Salvo há ${diff}s`;
    return `Salvo há ${Math.round(diff / 60)}min`;
  }
  return '';
}

/**
 * Dropdown "3 pontos" no canto do toolbar. Ações relacionadas ao TEMA
 * INTEIRO (não à section selecionada, não à página) ficam aqui.
 *
 * Hoje: só "Baixar tema" (LC-1466 — download do zip do tema atual).
 * Próximas: "Duplicar tema", "Renomear tema", "Importar tema", etc.
 * Ficam soltas do menu porque são raras e o header já tá cheio.
 *
 * Fecha ao clicar fora (mousedown global) e ao pressionar Escape.
 */
function ThemeMenu({ session }: { session: import('../api/client').EditorSession | null }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handleDownload() {
    if (!session || downloading) return;
    setDownloading(true);
    try {
      await downloadTheme(session);
      setOpen(false);
    } catch (err: any) {
      // Toast global não tá disponível aqui — alert é o mínimo pra
      // não engolir erro em silêncio.
      alert(`Erro ao baixar tema: ${err?.message || 'desconhecido'}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
        title="Menu do tema"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-lg border border-black/10 bg-white text-[13px] text-neutral-800 shadow-xl"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleDownload}
            disabled={!session || downloading}
            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" x2="12" y1="15" y2="3" />
            </svg>
            <span className="flex-1">Baixar tema</span>
            {downloading && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="animate-spin">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function Toolbar() {
  const device = useEditor((s) => s.device);
  const setDevice = useEditor((s) => s.setDevice);
  const mode = useEditor((s) => s.mode);
  const setMode = useEditor((s) => s.setMode);
  const status = useEditor((s) => s.saveStatus);
  const page = useEditor((s) => s.page);
  const pages = useEditor((s) => s.metadata?.data.pages || []);
  const setPage = useEditor((s) => s.setPage);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const canUndo = useEditor((s) => s.past.length > 0);
  const canRedo = useEditor((s) => s.future.length > 0);
  const session = useEditor((s) => s.session);

  // Store info — pré-carregado em background pra "Ver loja" responder
  // instantaneamente. Se a session já tem storeHost, pula o fetch
  // (significa que o editor foi aberto via o domínio do cliente).
  const [storeInfo, setStoreInfo] = useState<{
    domain_store: string | null;
    loaded: boolean;
  }>({ domain_store: null, loaded: false });
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [verLojaBusy, setVerLojaBusy] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  useEffect(() => {
    if (!session) return;
    if (session.storeHost) {
      // Já temos o domínio — não precisa consultar paycentral
      setStoreInfo({ domain_store: session.storeHost, loaded: true });
      return;
    }
    let cancelled = false;
    fetchStoreInfo(session)
      .then((info) => {
        if (!cancelled) {
          setStoreInfo({ domain_store: info.domain_store, loaded: true });
        }
      })
      .catch(() => {
        // Falha ao consultar paycentral — assume sem domínio pra forçar
        // o modal (melhor pedir pra configurar que abrir loja quebrada).
        if (!cancelled) setStoreInfo({ domain_store: null, loaded: true });
      });
    return () => { cancelled = true; };
  }, [session]);

  async function handleVerLoja() {
    if (!session) return;

    // Se já temos o domínio cacheado, abre direto
    if (storeInfo.loaded) {
      if (storeInfo.domain_store) {
        const url = /^https?:/i.test(storeInfo.domain_store)
          ? storeInfo.domain_store
          : `https://${storeInfo.domain_store}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setShowDomainModal(true);
      }
      return;
    }

    // Cache ainda não pronto — força fetch agora
    setVerLojaBusy(true);
    try {
      const info = await fetchStoreInfo(session);
      setStoreInfo({ domain_store: info.domain_store, loaded: true });
      if (info.domain_store) {
        const url = /^https?:/i.test(info.domain_store)
          ? info.domain_store
          : `https://${info.domain_store}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setShowDomainModal(true);
      }
    } catch {
      setStoreInfo({ domain_store: null, loaded: true });
      setShowDomainModal(true);
    } finally {
      setVerLojaBusy(false);
    }
  }

  // Visual-mode save orchestration
  const save = useEditor((s) => s.save);
  const visualDirty = useEditor(dirtyCount);
  const visualDirtyPages = useEditor(dirtyPages);

  // Code-mode save orchestration
  const codeStatus = useCode((s) => s.saveStatus);
  const codeSaveAll = useCode((s) => s.saveAll);
  const hasDirtyCode = useCode((s) =>
    Object.values(s.tabs).some((t) => t.buffer !== t.original)
  );
  const dirtyCodeCount = useCode((s) =>
    Object.values(s.tabs).filter((t) => t.buffer !== t.original).length
  );

  const isSaving = status.state === 'saving' || codeStatus.state === 'saving';
  const hasDirty = mode === 'code' ? hasDirtyCode : visualDirty > 0;
  const dirty = mode === 'code' ? dirtyCodeCount : visualDirty;

  // Preview context (product/collection picker)
  const products = useEditor((s) => s.metadata?.data.products || []);
  const collections = useEditor((s) => s.metadata?.data.collections || []);
  const previewProductSlug = useEditor((s) => s.previewProductSlug);
  const previewCollectionId = useEditor((s) => s.previewCollectionId);
  const setPreviewProduct = useEditor((s) => s.setPreviewProduct);
  const setPreviewCollection = useEditor((s) => s.setPreviewCollection);

  // Inspector toggle
  const inspectorOpen = useEditor((s) => s.inspectorOpen);
  const setInspectorOpen = useEditor((s) => s.setInspectorOpen);
  // Preview-editable toggle (hover outlines + click-to-select in iframe)
  const previewEditable = useEditor((s) => s.previewEditable);
  const setPreviewEditable = useEditor((s) => s.setPreviewEditable);

  // Cart type (from theme globals — drawer | message | page)
  const cartType = useEditor((s) => (s.globalSettings?.cart_type as string | undefined) || 'page');

  const setCartPreviewMode = useEditor((s) => s.setCartPreviewMode);
  const select = useEditor((s) => s.select);

  /**
   * Clicking "Carrinho" no editor navega pra `/cart` E auto-seleciona
   * a section que controla o modo de carrinho ATIVO no momento:
   *
   *   cart_type === 'page'     → seleciona main-cart (na page template)
   *   cart_type === 'drawer'   → seleciona cart-drawer-config (no
   *                              section group cart-drawer-group)
   *   cart_type === 'message'  → mesmo que drawer (toast usa as mesmas
   *                              configs de cart-drawer-config; não
   *                              tem section dedicada de "message")
   *
   * Pedido do user: "no menu, quando a pessoa clicar na pagina carrinho,
   * tem que abrir a configuracao do carrinho que a pessoa está. se a
   * pagina a pagina, se o drawer o drawer, mensagem mensagem".
   *
   * Antes o selectPage só navegava pra `/cart` e deixava a seleção
   * herdada da página anterior — usuário tinha que CAÇAR o inspector
   * certo no painel lateral. Agora abre direto com a section certa
   * pré-selecionada pra editar.
   *
   * Drawer/message: a `/cart` page renderiza o drawer full-screen
   * automaticamente (CSS auto-abre quando URL é /cart), então mesmo
   * em modo drawer a navegação pra /cart faz sentido — o seller vê o
   * drawer em ação enquanto edita.
   */
  async function selectPage(next: string) {
    if (next === 'cart') {
      const mode = cartType as 'drawer' | 'message' | 'page';
      const isDrawer = mode === 'drawer';
      const isMessage = mode === 'message';
      // Mantém compat com themes que checam cartPreviewMode.
      setCartPreviewMode(isDrawer ? 'drawer' : isMessage ? 'message' : null);

      // Navegação primeiro — `setPage` é async e popula sections do
      // template + cartDrawerGroup. Sem o await, getState() abaixo
      // vê o estado da página anterior e a seleção falha (section
      // ID não existe ainda no novo template).
      await setPage('cart');

      // Pega o estado APÓS o setPage resolver. Acesso direto via
      // getState() em vez de subscriber pra evitar stale closures.
      const state = useEditor.getState();

      if (isDrawer || isMessage) {
        // Drawer / mensagem: seleciona a primeira section do
        // cart-drawer-group (default é `drawer-config` tipo
        // cart-drawer-config — definido no .json do section group).
        const order = state.cartDrawerGroup?.order ?? [];
        const firstId = order[0];
        if (firstId) {
          select({ kind: 'section', group: 'cart-drawer-group', sectionId: firstId });
        }
      } else {
        // Page mode: encontra a section com type 'main-cart' dentro
        // do template da /cart. Em geral é a única — mas iteramos o
        // order pra garantir que pegamos a primeira instância no
        // caso de duplicatas (presets podem variar).
        const sections = state.sections;
        const order = state.order;
        const mainCartId = order.find((id) => sections[id]?.type === 'main-cart');
        if (mainCartId) {
          select({ kind: 'section', group: 'template', sectionId: mainCartId });
        }
      }
      return;
    }
    // Navegar pra qualquer outra página → limpa o cart preview mode
    // pra não re-disparar o drawer por engano.
    setCartPreviewMode(null);
    void setPage(next);
  }

  const cartLabel =
    cartType === 'drawer' ? 'Carrinho (gaveta)'
      : cartType === 'message' ? 'Carrinho (mensagem)'
      : 'Carrinho';

  /*
    Auto-re-seleciona a section correta quando o seller MUDA cart_type
    estando ALREADY na page 'cart'. Sem isso, o user mudava cart_type
    de drawer pra page no inspector global, mas o inspector da section
    continuava em cart-drawer-config (porque selectPage só roda em
    onChange do dropdown — clicar "cart" estando já em "cart" não
    dispara onChange).

    Bug reportado: "mesmo selecionando o carrinho pagina, ao tentar
    editar ele so mostra as config do drawer".

    Effect roda quando `cartType` ou `page` mudam — gate pra `page === 'cart'`
    pra não interferir em outras páginas. Pequeno setTimeout pra
    garantir que setPage anterior já populou state.sections / cartDrawerGroup
    (especialmente importante quando a mudança vem junto com page change).
  */
  useEffect(() => {
    if (page !== 'cart') return;
    const t = window.setTimeout(() => {
      const state = useEditor.getState();
      const isDrawerMode = cartType === 'drawer' || cartType === 'message';
      // Mantém cartPreviewMode em sync (afeta theme.js que escuta).
      state.setCartPreviewMode(
        cartType === 'drawer' ? 'drawer'
          : cartType === 'message' ? 'message'
          : null
      );
      if (isDrawerMode) {
        const order = state.cartDrawerGroup?.order ?? [];
        const firstId = order[0];
        if (firstId) {
          state.select({ kind: 'section', group: 'cart-drawer-group', sectionId: firstId });
        }
      } else {
        // Page mode → seleciona main-cart
        const sections = state.sections;
        const order = state.order;
        const mainCartId = order.find((id) => sections[id]?.type === 'main-cart');
        if (mainCartId) {
          state.select({ kind: 'section', group: 'template', sectionId: mainCartId });
        }
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [cartType, page]);

  // Warn before accidental F5 / close when there are unsaved changes
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasDirty]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (mod && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return (
    <header className="flex h-14 shrink-0 items-center justify-between bg-primary px-3 text-text-inverse">
      {/* ---------- Left ---------- */}
      <div className="flex items-center gap-2">
        <ThemeMenu session={session} />


        <div className="flex items-center gap-2 rounded-lg bg-white/[0.09] px-2.5 py-1.5 text-[13px]">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-green-400 to-emerald-600 text-[10px] font-bold text-white">
            {(session?.tenantId?.[0] || 'L').toUpperCase()}
          </span>
          <span className="max-w-[160px] truncate font-medium">
            {session?.tenantId || 'loja'}
          </span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-60">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </div>
      </div>

      {/* ---------- Center: mode toggle + page picker ---------- */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5 rounded-lg bg-white/[0.08] p-0.5">
          <ModeBtn active={mode === 'visual'} onClick={() => setMode('visual')} title="Editor visual">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
            <span className="ml-1.5 text-[12px] font-medium">Visual</span>
          </ModeBtn>
          <ModeBtn active={mode === 'code'} onClick={() => setMode('code')} title="Editor de código">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            <span className="ml-1.5 text-[12px] font-medium">Código</span>
          </ModeBtn>
        </div>

        {mode === 'visual' && (
          <div className="relative flex items-center gap-1 rounded-lg bg-white/[0.08] px-1 py-1 text-[13px]">
            <button className="flex h-7 w-7 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white" title="Página anterior">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>

            <select
              className="cursor-pointer bg-transparent pr-6 text-white outline-none [appearance:none]"
              value={page}
              onChange={(e) => selectPage(e.target.value)}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round'><path d='m6 9 6 6 6-6'/></svg>\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0 center',
                paddingLeft: 10,
              }}
            >
              <option value="index" className="text-text">Página inicial</option>
              <option value="product" className="text-text">Produto</option>
              <option value="collection" className="text-text">Coleção</option>
              <option value="cart" className="text-text">{cartLabel}</option>
              <option value="page" className="text-text">Página</option>
              <option value="404" className="text-text">404</option>
              {pages.map((p) => (
                <option key={p.id} value={`page-${p.handle}`} className="text-text">
                  {p.title}
                </option>
              ))}
            </select>

            <button className="flex h-7 w-7 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white" title="Próxima página">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        )}

        {/* Preview context pickers: only meaningful on detail pages */}
        {mode === 'visual' && page === 'product' && (
          <PreviewPicker
            icon={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 7 12 3 4 7m16 0v10l-8 4m8-14-8 4m0 0L4 7m8 4v10L4 17V7" />
              </svg>
            }
            value={previewProductSlug || ''}
            onChange={(v) => void setPreviewProduct(v || null)}
            emptyLabel="Produto padrão"
            options={products.map((p: any) => ({
              value: String(p.handle || p.id || p.uuid || ''),
              label: p.title || p.name || '(sem título)',
            }))}
          />
        )}
        {mode === 'visual' && page === 'collection' && (
          <PreviewPicker
            icon={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            }
            value={previewCollectionId || ''}
            onChange={(v) => void setPreviewCollection(v || null)}
            emptyLabel="Coleção padrão"
            options={collections.map((c: any) => ({
              value: String(c.handle || c.id || c.uuid || ''),
              label: c.title || c.name || '(sem título)',
            }))}
          />
        )}
      </div>

      {/* ---------- Right ---------- */}
      <div className="flex items-center gap-2">
        {mode === 'visual' && (
          <>
            <div className="flex items-center gap-0.5 rounded-lg bg-white/[0.08] p-0.5">
              <DeviceBtn active={device === 'desktop'} onClick={() => setDevice('desktop')} title="Desktop">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="13" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                </svg>
              </DeviceBtn>
              <DeviceBtn active={device === 'mobile'} onClick={() => setDevice('mobile')} title="Mobile">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="2" width="12" height="20" rx="2.5" />
                  <path d="M11 18h2" />
                </svg>
              </DeviceBtn>
            </div>

            <div className="mx-1 flex items-center gap-0.5">
              <IconBtn disabled={!canUndo} title="Desfazer (Ctrl+Z)" onClick={undo}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-15-6.7L3 13" />
                </svg>
              </IconBtn>
              <IconBtn disabled={!canRedo} title="Refazer (Ctrl+Shift+Z)" onClick={redo}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 15-6.7L21 13" />
                </svg>
              </IconBtn>
            </div>
          </>
        )}

        {/* Status pill — shows save status for whichever mode is active */}
        {mode === 'visual' && status.state !== 'idle' && (
          <span className="mr-1 min-w-[80px] text-right text-xs text-white/70" title={status.message}>
            {status.state === 'saving' && (
              <span className="inline-flex items-center gap-1.5">
                <span className="lc-spin h-3 w-3 rounded-full border border-white/30 border-t-white" />
                {status.progress
                  ? `Salvando ${status.progress.current}/${status.progress.total}`
                  : 'Salvando'}
              </span>
            )}
            {status.state === 'saved' && statusText('saved', status.lastSavedAt)}
            {status.state === 'error' && <span className="text-[#ffb4a8]">Erro ao salvar</span>}
          </span>
        )}
        {mode === 'code' && codeStatus.state !== 'idle' && (
          <span className="mr-1 min-w-[68px] text-right text-xs text-white/70">
            {codeStatus.state === 'saving' && (
              <span className="inline-flex items-center gap-1.5">
                <span className="lc-spin h-3 w-3 rounded-full border border-white/30 border-t-white" />
                Salvando
              </span>
            )}
            {codeStatus.state === 'saved' && statusText('saved', codeStatus.at)}
            {codeStatus.state === 'error' && <span className="text-[#ffb4a8]">Erro</span>}
          </span>
        )}

        {mode === 'visual' && (
          <IconBtn
            title={
              previewEditable
                ? 'Desativar seletor (ver loja como visitante)'
                : 'Ativar seletor (clicar para editar seções)'
            }
            onClick={() => setPreviewEditable(!previewEditable)}
            active={previewEditable}
          >
            {/* Cursor / arrow on top of a framed preview — "on" when the
                overlay is active. A slash crosses it when disabled so the
                state is readable at a glance. */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4l7 17 2.5-7L20.5 11z" />
              {!previewEditable && <path d="M3 3l18 18" />}
            </svg>
          </IconBtn>
        )}

        {mode === 'visual' && (
          <IconBtn
            title={inspectorOpen ? 'Esconder painel' : 'Mostrar painel'}
            onClick={() => setInspectorOpen(!inspectorOpen)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M15 3v18" />
              {!inspectorOpen && <path d="m10 8 4 4-4 4" />}
              {inspectorOpen && <path d="m19 8-4 4 4 4" />}
            </svg>
          </IconBtn>
        )}

        {mode === 'visual' && (
          <button
            onClick={() => setReviewsOpen(true)}
            title="Avaliações"
            className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-transparent px-3 py-1.5 text-[13px] font-medium text-white hover:bg-white/10"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Avaliações
          </button>
        )}
        {reviewsOpen && <ReviewsPanel onClose={() => setReviewsOpen(false)} />}
        <button
          onClick={handleVerLoja}
          disabled={!session || verLojaBusy}
          title={
            verLojaBusy
              ? 'Verificando domínio…'
              : storeInfo.loaded && storeInfo.domain_store
                ? `Abrir ${storeInfo.domain_store} em nova aba`
                : storeInfo.loaded
                  ? 'Domínio não configurado — clique pra configurar'
                  : 'Ver loja'
          }
          className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-transparent px-3 py-1.5 text-[13px] font-medium text-white hover:bg-white/10 disabled:opacity-50"
        >
          {verLojaBusy ? (
            <span className="lc-spin h-3 w-3 rounded-full border border-white/30 border-t-white" />
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          )}
          Ver loja
        </button>
        {showDomainModal && (
          <DomainRequiredModal
            tenantId={session?.tenantId || ''}
            onClose={() => setShowDomainModal(false)}
          />
        )}
        <button
          className="flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-1.5 text-[13px] font-semibold text-primary shadow-button transition-opacity hover:bg-surface-hovered disabled:opacity-50"
          disabled={!hasDirty || isSaving}
          onClick={() => {
            if (mode === 'code') void codeSaveAll();
            else void save();
          }}
          title={
            mode === 'code'
              ? 'Salvar arquivos abertos (Ctrl+S)'
              : hasDirty
                ? `Salvar ${dirty} altera${dirty === 1 ? 'ção' : 'ções'}${
                    visualDirtyPages.length > 1
                      ? ` em ${visualDirtyPages.length} páginas (${visualDirtyPages.join(', ')})`
                      : ''
                  }`
                : 'Sem alterações'
          }
        >
          {isSaving ? (
            <>
              <span className="lc-spin h-3 w-3 rounded-full border border-primary/30 border-t-primary" />
              Salvando…
            </>
          ) : (
            <>
              Salvar
              {hasDirty && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-interactive px-1.5 text-[11px] font-bold text-white">
                  {dirty}
                </span>
              )}
            </>
          )}
        </button>
      </div>
    </header>
  );
}

function DeviceBtn({
  active, onClick, title, children,
}: {
  active: boolean;
  onClick(): void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={
        'flex h-7 w-7 items-center justify-center rounded-md transition-colors ' +
        (active
          ? 'bg-white text-primary'
          : 'text-white/70 hover:bg-white/10 hover:text-white')
      }
    >
      {children}
    </button>
  );
}

function PreviewPicker({
  icon, value, onChange, options, emptyLabel,
}: {
  icon: React.ReactNode;
  value: string;
  onChange(v: string): void;
  options: Array<{ value: string; label: string }>;
  emptyLabel: string;
}) {
  return (
    <div className="flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.08] pl-2 pr-1 text-[13px] text-white/90">
      <span className="text-white/60">{icon}</span>
      <select
        className="cursor-pointer bg-transparent pr-5 text-white outline-none [appearance:none]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={options.find((o) => o.value === value)?.label || emptyLabel}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round'><path d='m6 9 6 6 6-6'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0 center',
          maxWidth: 160,
        }}
      >
        <option value="" className="text-text">{emptyLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value} className="text-text">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ModeBtn({
  active, onClick, title, children,
}: {
  active: boolean;
  onClick(): void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={
        'flex h-7 items-center rounded-md px-2 transition-colors ' +
        (active
          ? 'bg-white text-primary'
          : 'text-white/70 hover:bg-white/10 hover:text-white')
      }
    >
      {children}
    </button>
  );
}

/**
 * Modal mostrado quando o lojista clica "Ver loja" mas ainda não
 * configurou um domínio próprio. Antes a gente abria o subdomínio
 * preview e ele virava de fato a "loja pública" — o que NÃO queremos,
 * porque o lojista precisa ter um domínio dele.
 *
 * Decisão produto: bloquear esse atalho e forçar configuração.
 * Botão primário leva pro painel paycentral de domínios.
 */
function DomainRequiredModal({
  tenantId,
  onClose,
}: {
  tenantId: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="domain-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 text-text shadow-2xl">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.485 0 4.733 1.008 6.364 2.636" />
            <path d="m12 8 .01 5" />
            <path d="M12 16h.01" />
          </svg>
        </div>
        <h2 id="domain-modal-title" className="mb-2 text-lg font-semibold">
          Configure um domínio pra ver sua loja
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-text-subdued">
          Pra publicar sua loja você precisa apontar um domínio próprio
          (ex: <code className="rounded bg-surface px-1 py-0.5 text-xs">loja.seusite.com.br</code>).
          O endereço de preview da Luna é apenas pra uso interno do editor —
          ele não funciona como vitrine pública.
        </p>
        <div className="mb-5 rounded-lg border border-border bg-surface px-3 py-2.5 text-xs text-text-subdued">
          <div className="mb-1 font-medium text-text">Como configurar:</div>
          <ol className="ml-4 list-decimal space-y-0.5">
            <li>No seu provedor de DNS, crie um registro CNAME</li>
            <li>Aponte pra <code className="text-primary">engine.lunacheckout.com</code></li>
            <li>Cadastre o domínio no painel da Luna</li>
          </ol>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-medium text-text-subdued hover:bg-surface"
          >
            Mais tarde
          </button>
          <a
            href={ADMIN_DOMAIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-text-inverse hover:opacity-90"
          >
            Configurar domínio
          </a>
        </div>
        {tenantId && (
          <div className="mt-3 text-center text-[11px] text-text-subdued">
            Loja: <code>{tenantId}</code>
          </div>
        )}
      </div>
    </div>
  );
}

function IconBtn({
  disabled, title, onClick, children, active,
}: {
  disabled?: boolean;
  title: string;
  onClick(): void;
  children: React.ReactNode;
  /** When true, renders with a filled background to show it's "on". */
  active?: boolean;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={
        'flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ' +
        (active
          ? 'bg-white/[0.18] text-white enabled:hover:bg-white/25'
          : 'text-white/80 enabled:hover:bg-white/10 enabled:hover:text-white')
      }
    >
      {children}
    </button>
  );
}
